import type { ReactNode } from "react"

export type DynamicScreenOptions = {
  headerRight?: () => ReactNode
  headerLeft?: () => ReactNode
  title?: string
  headerShown?: boolean
  headerBackVisible?: boolean
}

const EMPTY_OPTIONS: DynamicScreenOptions = Object.freeze({})

const optionsByPath = new Map<string, DynamicScreenOptions>()
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((listener) => listener())
}

function isSameOptions(a: DynamicScreenOptions, b: DynamicScreenOptions) {
  return (
    a.headerRight === b.headerRight &&
    a.headerLeft === b.headerLeft &&
    a.title === b.title &&
    a.headerShown === b.headerShown &&
    a.headerBackVisible === b.headerBackVisible
  )
}

export function setScreenOptions(pathname: string, options: DynamicScreenOptions) {
  const prev = optionsByPath.get(pathname) ?? EMPTY_OPTIONS
  const next: DynamicScreenOptions = { ...prev, ...options }

  if (options.headerRight === undefined && "headerRight" in options) {
    delete next.headerRight
  }
  if (options.headerLeft === undefined && "headerLeft" in options) {
    delete next.headerLeft
  }

  if (isSameOptions(prev, next)) return

  if (Object.keys(next).length === 0) {
    optionsByPath.delete(pathname)
  } else {
    optionsByPath.set(pathname, next)
  }
  notify()
}

export function getScreenOptions(pathname: string): DynamicScreenOptions {
  return optionsByPath.get(pathname) ?? EMPTY_OPTIONS
}

export function clearScreenOptions(pathname: string) {
  if (!optionsByPath.has(pathname)) return
  optionsByPath.delete(pathname)
  notify()
}

export function subscribeScreenOptions(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
